import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';

const HelpArticleMigration = () => {
  return (
    <div>
      <SeoHead
        title="Migrating to CodeRom | CodeRom Help"
        description="Step-by-step guide to migrating your eBay dropshipping business to CodeRom."
      />
      <HelpBreadcrumbSchema slug="migration" name="Migrating to Coderom" />
      <Navbar />
      <div className="help-article-page">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">

              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Migrating to Coderom</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <span className="category" style={{ background: '#f0fdf4', color: '#16a34a' }}>🚀 Migration</span>
                <h1>Migrating to Coderom</h1>
                <p className="article-subtitle">A step-by-step guide to moving your eBay dropshipping operation to Coderom from another tool.</p>
                <div className="article-meta">
                  <span className="read-time">⏱ 10 min read</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h4>On this page</h4>
                <ul>
                  <li><a href="#step1">Step 1: Before you start</a></li>
                  <li><a href="#step2">Step 2: Connect your eBay account</a></li>
                  <li><a href="#step3">Step 3: Add your products</a></li>
                  <li><a href="#step4">Step 4: Review your draft products</a></li>
                  <li><a href="#step5">Step 5: Publish to eBay</a></li>
                  <li><a href="#step6">Step 6: Post-migration checklist</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Before you start</h2>
                  <p>Before switching to Coderom, take a few minutes to prepare. A little planning now will make the migration much smoother.</p>
                  <h3>What to do before you migrate</h3>
                  <ul>
                    <li><strong>Note your active listings</strong> — record how many active eBay listings you currently have so you can verify the count after migration</li>
                    <li><strong>Export your product data</strong> — if your current tool supports CSV export, download a copy of your product list (SKUs, eBay Item IDs, ASINs, prices)</li>
                    <li><strong>Choose a quiet migration window</strong> — pick a time when your sales volume is lower (e.g. a weekday morning) to minimise disruption</li>
                    <li><strong>Keep your old tool running</strong> — do not cancel your old subscription until you have verified all your products are live in Coderom</li>
                  </ul>
                  <div className="tip-box">
                    <span className="tip-icon">💡</span>
                    <div className="tip-content">
                      <strong>Run both tools in parallel</strong> for 5–7 days. This gives you time to verify everything is working correctly before fully switching over.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Connect your eBay account</h2>
                  <p>The first thing to do in Coderom is connect your eBay seller account. This allows Coderom to manage your listings, update prices, and upload tracking.</p>
                  <ol>
                    <li>Go to <strong>Settings → eBay Connection</strong></li>
                    <li>Click <strong>Connect eBay Account</strong></li>
                    <li>Sign in to your eBay seller account in the popup</li>
                    <li>Click <strong>Agree</strong> to grant Coderom the required permissions</li>
                    <li>Once connected, you will see a green "Connected" status with your eBay username</li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">💡</span>
                    <div className="tip-content">
                      <strong>Multiple stores:</strong> If you sell on more than one eBay marketplace (e.g. ebay.co.uk and ebay.de), you can connect additional stores from <strong>Settings → Store Management</strong>.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Add your products</h2>
                  <p>Coderom sources products from Amazon. To get your products into Coderom, you need to provide the Amazon ASIN for each item you want to sell. There are two ways to do this:</p>

                  <h3>Option A: Bulk Import via CSV (recommended for large catalogs)</h3>
                  <p>If you have many products, the fastest way is to upload a CSV file containing your ASINs:</p>
                  <ol>
                    <li>Go to <strong>Product Import</strong> in the main menu</li>
                    <li>Download the CSV template</li>
                    <li>Fill in one ASIN per row</li>
                    <li>Upload the completed CSV file</li>
                    <li>Coderom will import and scrape each product automatically</li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">💡</span>
                    <div className="tip-content">
                      <strong>Finding ASINs:</strong> The ASIN is the 10-character product identifier in the Amazon URL (e.g. <strong>B08N5WRWNW</strong>). If your current tool tracks supplier URLs, the ASIN is embedded in the Amazon product link.
                    </div>
                  </div>

                  <h3>Option B: Search and add one by one</h3>
                  <p>For smaller catalogs or when adding individual products:</p>
                  <ol>
                    <li>Use the search bar at the top of the dashboard</li>
                    <li>Enter an Amazon ASIN or paste an Amazon product URL</li>
                    <li>Coderom will fetch the product details and add it to your Draft Products</li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">⏱</span>
                    <div className="tip-content">
                      <strong>Scraping takes time:</strong> After import, Coderom needs to scrape each product from Amazon to get the latest price and stock data. Large imports (500+ products) may take a few minutes to fully process.
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Review your draft products</h2>
                  <p>After import, your products appear in <strong>Products → Draft Products</strong>. Before publishing to eBay, review each product to make sure everything looks correct.</p>
                  <h3>What to check</h3>
                  <ul>
                    <li><strong>Title</strong> — Coderom generates an eBay-optimised title automatically. You can edit it or use AI title optimisation to improve it further</li>
                    <li><strong>Price</strong> — verify that your profit margin settings are correct (Settings → Pricing). The eBay price is calculated automatically based on the Amazon price plus your margin</li>
                    <li><strong>Stock</strong> — check that stock levels have been scraped correctly. Products showing "Out of Stock" may need a moment to update</li>
                    <li><strong>Category</strong> — Coderom maps Amazon categories to eBay automatically. Review any products with unmapped categories</li>
                    <li><strong>Business policies</strong> — ensure your payment, shipping, and return policies are configured in Settings before publishing</li>
                  </ul>
                  <div className="tip-box">
                    <span className="tip-icon">🤖</span>
                    <div className="tip-content">
                      <strong>AI listing optimisation:</strong> Enable AI optimisation in Settings to automatically rewrite titles and descriptions for better eBay SEO. You can apply it in bulk from the Draft Products page.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Publish to eBay</h2>
                  <p>Once you are happy with your draft products, you can publish them to eBay. Coderom will create new eBay listings for each product.</p>
                  <ol>
                    <li>Go to <strong>Products → Draft Products</strong></li>
                    <li>Select the products you want to publish (use the checkbox to select all)</li>
                    <li>Click <strong>Publish to eBay</strong></li>
                    <li>Coderom will submit the listings via the eBay API — this usually takes a few seconds per listing</li>
                    <li>Published products move to <strong>Products → Active Listings</strong></li>
                  </ol>
                  <div className="tip-box">
                    <span className="tip-icon">⚠️</span>
                    <div className="tip-content">
                      <strong>Duplicate listings:</strong> If you are migrating from another tool, your existing eBay listings may already be live. Publishing the same product again will create a duplicate. Either end your old listings first, or wait until you are ready to fully switch over.
                    </div>
                  </div>
                  <div className="tip-box">
                    <span className="tip-icon">✅</span>
                    <div className="tip-content">
                      <strong>Ready to switch:</strong> Once your products are live in Coderom and you have verified prices and stock are updating correctly, you can safely cancel your old tool's subscription.
                    </div>
                  </div>
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Post-migration checklist</h2>
                  <p>After your products are live, take a few minutes to configure the key Coderom features that will automate your store going forward.</p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Where</th>
                          <th>Why it matters</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Configure repricing rules</strong></td>
                          <td>Settings → Pricing</td>
                          <td>Set your profit margin, eBay fees, and min/max prices so Coderom keeps your prices competitive automatically</td>
                        </tr>
                        <tr>
                          <td><strong>Enable stock monitoring</strong></td>
                          <td>Settings → Stock</td>
                          <td>Coderom will automatically mark listings as out of stock when Amazon stock runs out</td>
                        </tr>
                        <tr>
                          <td><strong>Set up Safety & Protection</strong></td>
                          <td>Settings → Safety</td>
                          <td>Load VeRO brands and blocked keywords to protect your store from policy violations</td>
                        </tr>
                        <tr>
                          <td><strong>Enable daily reports</strong></td>
                          <td>Settings → Reports</td>
                          <td>Get a daily email summary of your sales, profit, and stock alerts</td>
                        </tr>
                        <tr>
                          <td><strong>Connect Email tracking</strong></td>
                          <td>Settings → Email</td>
                          <td>Automatically upload tracking numbers to eBay from your Amazon dispatch emails</td>
                        </tr>
                        <tr>
                          <td><strong>Cancel your old tool</strong></td>
                          <td>Your old tool's billing settings</td>
                          <td>Once you have verified everything is working, cancel to avoid double billing</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="tip-box">
                    <span className="tip-icon">🎉</span>
                    <div className="tip-content">
                      <strong>You're live on Coderom!</strong> If you have any questions during migration, visit our Help Center or contact support at <strong>support@coderom.com</strong>.
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <div className="next-steps">
                    <Link to="/help/pricing" className="next-step-card">
                      <span className="next-step-icon">💰</span>
                      <div className="next-step-content">
                        <h4>Smart repricing</h4>
                        <p>Set up your pricing rules to stay competitive automatically</p>
                      </div>
                    </Link>
                    <Link to="/help/safety-protection" className="next-step-card">
                      <span className="next-step-icon">🛡️</span>
                      <div className="next-step-content">
                        <h4>Safety & Protection</h4>
                        <p>Protect your store from VeRO claims with blocked brands and keywords</p>
                      </div>
                    </Link>
                    <Link to="/help/gmail-tracking" className="next-step-card">
                      <span className="next-step-icon">📧</span>
                      <div className="next-step-content">
                        <h4>Gmail tracking</h4>
                        <p>Automatically upload tracking numbers from your Amazon emails</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was this helpful */}
                <div className="helpful-section">
                  <p className="helpful-text">Was this article helpful?</p>
                  <div className="helpful-buttons">
                    <button className="helpful-btn">👍 Yes</button>
                    <button className="helpful-btn">👎 No</button>
                  </div>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
      <GuestChatWidget />
      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }
        .breadcrumb-section { margin-bottom: 24px; font-size: 14px; color: #64748b; }
        .breadcrumb-link { color: #6366f1; text-decoration: none; }
        .breadcrumb-link:hover { text-decoration: underline; }
        .breadcrumb-separator { margin: 0 8px; }
        .article-header { margin-bottom: 32px; }
        .article-header h1 { font-size: 36px; font-weight: 700; color: #1e293b; margin: 16px 0 12px; }
        .article-subtitle { font-size: 18px; color: #475569; margin-bottom: 16px; }
        .article-meta { display: flex; gap: 16px; }
        .read-time, .category { font-size: 13px; padding: 4px 12px; border-radius: 20px; }
        .read-time { background: #f1f5f9; color: #64748b; }
        .category { font-weight: 600; }
        .table-of-contents { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px 24px; margin-bottom: 40px; }
        .table-of-contents h4 { font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
        .table-of-contents ul { list-style: none; padding: 0; margin: 0; }
        .table-of-contents li { margin-bottom: 8px; }
        .table-of-contents a { color: #6366f1; text-decoration: none; font-size: 14px; }
        .table-of-contents a:hover { text-decoration: underline; }
        .content-section { margin-bottom: 48px; }
        .content-section h2 { font-size: 24px; font-weight: 700; color: #1e293b; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0; }
        .content-section h3 { font-size: 18px; font-weight: 600; color: #374151; margin: 24px 0 12px; }
        .content-section p { color: #475569; line-height: 1.7; margin-bottom: 16px; }
        .content-section ul, .content-section ol { color: #475569; padding-left: 24px; margin-bottom: 16px; }
        .content-section li { margin-bottom: 8px; line-height: 1.6; }
        .tip-box { display: flex; gap: 12px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 16px; margin: 20px 0; }
        .tip-icon { font-size: 20px; flex-shrink: 0; }
        .tip-content { font-size: 14px; color: #1e40af; line-height: 1.6; }
        .tip-content strong { color: #1e3a8a; }
        .settings-table { margin: 20px 0; overflow-x: auto; }
        .settings-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .settings-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e2e8f0; }
        .settings-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; color: #475569; vertical-align: top; }
        .settings-table tbody tr:hover { background: #f8fafc; }
        .whats-next-section { margin-top: 48px; padding-top: 32px; border-top: 2px solid #e2e8f0; }
        .whats-next-section h2 { font-size: 22px; font-weight: 700; color: #1e293b; margin-bottom: 20px; }
        .next-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
        .next-step-card { display: flex; gap: 12px; padding: 16px; background: white; border: 1px solid #e2e8f0; border-radius: 10px; text-decoration: none; transition: all 0.2s; }
        .next-step-card:hover { border-color: #6366f1; box-shadow: 0 4px 12px rgba(99,102,241,0.1); }
        .next-step-icon { font-size: 24px; flex-shrink: 0; }
        .next-step-content h4 { font-size: 14px; font-weight: 600; color: #1e293b; margin: 0 0 4px; }
        .next-step-content p { font-size: 13px; color: #64748b; margin: 0; }
        .helpful-section { text-align: center; padding: 32px; background: #f8fafc; border-radius: 12px; margin-top: 32px; }
        .helpful-text { font-size: 16px; color: #374151; margin-bottom: 16px; }
        .helpful-buttons { display: flex; gap: 12px; justify-content: center; }
        .helpful-btn { padding: 8px 24px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; cursor: pointer; font-size: 14px; transition: all 0.2s; }
        .helpful-btn:hover { border-color: #6366f1; color: #6366f1; }
        @media (max-width: 768px) {
          .help-article-page { padding: 80px 0 60px; }
          .article-header h1 { font-size: 28px; }
          .next-steps { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default HelpArticleMigration;
